import type { FundPriceItem } from '@festina/unitlink-general-types/unitlink'
import { notEmpty } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import {
  createColumnDefiner,
  SortableTable,
} from '@festina-life/ui/listsAndTables'
import { Card, CardBody, CardHeader } from '@festina-life/ui/structure'

import { useAmountFormatter } from '../../hooks/useAmountFormatter'
import { useHook } from '../../lenteHooks'
import { FundLink } from '../../pages/fund/FundLink'
import { FundName } from './FundName'

type Props = {
  tradeCycleId: string
}

const defineColumn = createColumnDefiner<FundPriceItem>()

export const TradeCycleTradePriceSellAndBuy = ({ tradeCycleId }: Props) => {
  const translate = useTranslate()
  const tenantId = useHook('routing.tenant.useTenantParam')()
  const { data } = useHook('fetchers.unitlink.useFetchSpecificTradeCycle')({
    tenantId,
    tradeCycleId,
  })
  const { formatWithDecimals } = useAmountFormatter()

  const fundPriceItems =
    data?.fundPriceItems
      ?.filter(notEmpty)
      .sort((a, b) => (a.isinKey ?? '').localeCompare(b.isinKey ?? '')) ?? []

  const columns = {
    fundName: defineColumn({
      heading: translate('tradeCycle.fundName'),
      keyPath: 'isinKey',
      sortable: false,
      formatter: (isinKey) => <>{isinKey && <FundName isinKey={isinKey} />}</>,
    }),
    isinkey: defineColumn({
      heading: translate('tradeCycle.isinkey'),
      keyPath: 'isinKey',
      sortable: false,
      formatter: (isinKey) =>
        isinKey ? <FundLink tenantId={tenantId} isinKey={isinKey} /> : '-',
    }),
    buyPrice: defineColumn({
      heading: translate('tradeCycle.buyPrice'),
      keyPath: 'buyPrice',
      formatter: formatWithDecimals,
    }),
    sellPrice: defineColumn({
      heading: translate('tradeCycle.sellPrice'),
      keyPath: 'sellPrice',
      formatter: formatWithDecimals,
    }),
  }

  return (
    <Card>
      <CardHeader title={translate('tradeCycle.tradePrices')} />
      <CardBody>
        <SortableTable columns={columns} data={fundPriceItems} />
      </CardBody>
    </Card>
  )
}
